/**
 * CreateTaskFieldWidget — Feb 2026
 *
 * Runtime widget for the ``create_task`` form-field type.  Provider
 * fills out (title, assignee, due_date, description) inside a note;
 * on note save the backend spawns a real Task linked to the
 * appointment + note.  After the task is created, the widget
 * collapses to a green "Task ✓" chip with a deep link to the task
 * drawer.
 *
 * Design choices:
 *  - We do NOT call the tasks API from this widget on every keystroke.
 *    The widget just collects the value and stuffs it into the note's
 *    ``form_field_values`` map.  The backend processor (see
 *    services/create_task_field_processor.py) fires the task once,
 *    on note save.  Avoids duplicate tasks if the user edits and
 *    re-saves.
 *  - On mount, we ping ``GET /form-tasks/resolve-assignee`` with the
 *    field's ``default_assignee_type`` to pre-fill the Assign To
 *    dropdown.  Falls back to an empty picker if no default resolves.
 *  - The form-field config (assignee_type, due_offset, title_template,
 *    description_label, task_category) is what the template designer
 *    customised — passed in via the ``field`` prop verbatim.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { formatDateStringForDisplay } from '../../utils/dateHelpers';
import {
  DEFAULT_RESCHEDULE_OPTIONS,
  RESCHEDULE_OPTIONS_BY_KEY,
  computeTargetDate,
} from './rescheduleOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "+offset" → YYYY-MM-DD relative to today.  Min 0 days.
const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + Math.max(0, parseInt(days, 10) || 0));
  return d.toISOString().slice(0, 10);
};

export const CreateTaskFieldWidget = ({
  field,
  value,
  onChange,
  client,
  readOnly = false,
  noteId,
}) => {
  // Form local state — kept in sync with the parent's value object.
  // The parent (Notes form) writes us back via ``onChange(nextValue)``
  // so the value stays in the note's ``form_field_values`` map and
  // gets persisted on save.
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Local cache of the assignee's display name once we've resolved
  // a stored UUID → real user.  Mirrors the underscore-prefixed
  // ``_assigned_to_name_cache`` we shove into the value object so
  // that re-opens after a save show a real name instead of a UUID.
  const [resolvedAssigneeName, setResolvedAssigneeName] = useState(null);

  // When the widget remounts with a persisted ``assigned_to`` UUID
  // but no cached display name (the underscore-prefixed cache is
  // stripped on round-trip through some serializers), resolve the
  // UUID to a user record so the dropdown / chip shows a real name
  // instead of a raw UUID.  Cheap one-shot GET per note open.
  useEffect(() => {
    const aid = (value || {}).assigned_to;
    if (!aid) return;
    if ((value || {})._assigned_to_name_cache) {
      setResolvedAssigneeName((value || {})._assigned_to_name_cache);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/users/${aid}`);
        const u = r?.data;
        const name = u?.full_name
          || `${u?.first_name || ''} ${u?.last_name || ''}`.trim()
          || u?.email
          || aid;
        if (!cancelled) {
          setResolvedAssigneeName(name);
          // Display-only: kept in local state. Do NOT write the cache back
          // through onChange — that dirtied the form on mount and triggered
          // autosaves on completed notes the user only VIEWED (iter 496).
        }
      } catch {
        if (!cancelled) setResolvedAssigneeName(aid);  // fall back to UUID
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [(value || {}).assigned_to]);

  // Pre-fill the assignee dropdown with the field's
  // ``default_assignee_type`` (scheduler / biller / supervisor /
  // supervisee / specific_user / current_user).  Only fires if the
  // current value doesn't already have an assignee picked.
  useEffect(() => {
    if (readOnly) return;
    if (value?.task_id) return;          // already spawned
    if (value?.assigned_to) return;       // user already picked
    const defaultType = field?.default_assignee_type
      || (field?.task_type_key ? 'task_type_route' : 'scheduler');
    if (!defaultType || defaultType === 'pick_at_runtime') return;
    let cancelled = false;
    (async () => {
      try {
        // Jun 2026 — 'task_type_route' resolves the admin-configured
        // routing target of the field's task type at runtime, so
        // re-routing a type applies without editing templates.
        const params = defaultType === 'task_type_route' && field?.task_type_key
          ? { assignee_type: 'task_type', task_type_key: field.task_type_key }
          : {
            assignee_type: defaultType === 'task_type_route' ? 'provider_scheduler' : defaultType,
            specific_user_id: field?.specific_user_id || undefined,
          };
        const r = await axios.get(`${API}/form-tasks/resolve-assignee`, { params });
        const u = r.data?.user;
        if (!cancelled && u) {
          onChange({
            ...(value || {}),
            assigned_to: u.id,
            _assigned_to_name_cache: u.full_name || u.email,
          });
        }
      } catch {
        // Non-fatal — the dropdown stays empty, provider picks manually.
      }
    })();
    return () => { cancelled = true; };
  }, [field?.default_assignee_type, field?.specific_user_id, field?.task_type_key, readOnly]);

  // Pre-fill the title input from the template's ``task_title_template``
  // so providers don't have to retype it every time (user request,
  // Feb 2026).  Only fires when the provider hasn't typed anything yet
  // AND the task hasn't already been spawned.  ``{client_name}`` is
  // substituted; other placeholders are left as literal so the
  // provider sees they need to fill them in.
  useEffect(() => {
    if (readOnly) return;
    if (value?.task_id) return;
    if (value?.title) return;
    const tpl = (field?.task_title_template || '').trim();
    if (!tpl) return;
    const filledTitle = tpl.replace(
      '{client_name}',
      client?.full_name || client?.first_name || '',
    ).trim();
    if (!filledTitle) return;
    onChange({ ...(value || {}), title: filledTitle });
    // eslint-disable-next-line
  }, [field?.task_title_template, client?.full_name, client?.first_name, readOnly]);

  // Lazy-load the full active-user list when the provider opens the
  // dropdown to pick a different assignee.  Avoids loading on every
  // note render.
  const ensureUsersLoaded = async () => {
    if (users.length > 0 || usersLoading) return;
    setUsersLoading(true);
    try {
      const r = await axios.get(`${API}/users`, { params: { active: true } });
      setUsers(Array.isArray(r.data) ? r.data : r.data?.users || []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // === After-save state — show a compact "Task created" chip with
  // a deep link to the drawer.  We don't render the form anymore
  // because the task is now a real first-class entity. ===
  if (value?.task_id) {
    const assigneeDisplay = resolvedAssigneeName
      || value._assigned_to_name_cache
      || value.assigned_to
      || '—';
    const isReschedule = (field?.action_type || 'reschedule_appointment') === 'reschedule_appointment';
    return (
      <div
        className="border border-emerald-300 bg-emerald-50 rounded-lg p-3 my-2 text-sm space-y-2"
        data-testid="create-task-field-spawned"
      >
        <div className="flex items-start gap-3">
          <span className="text-emerald-700 font-semibold whitespace-nowrap pt-0.5">✓ Task created</span>
          <div className="flex-1 min-w-0">
            <div className="text-emerald-900 font-medium break-words">{value.title || 'Task'}</div>
            {value.reschedule_in_label && (
              <div className="text-xs text-emerald-900 mt-1 font-medium" data-testid="create-task-field-spawned-reschedule-in">
                Reschedule in: <span className="font-semibold">{value.reschedule_in_label}</span>
              </div>
            )}
            {value.description && (
              <div className="text-xs text-emerald-900/80 mt-1 whitespace-pre-wrap break-words">
                {value.description}
              </div>
            )}
            <div className="text-[11px] text-emerald-700 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>Assigned to: <span className="font-medium">{assigneeDisplay}</span></span>
              {value.due_date && <span>Due: <span className="font-medium">{formatDateStringForDisplay(value.due_date)}</span></span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          {isReschedule && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
              onClick={() => {
                // Open the calendar focused on the linked appointment.
                // App.js listens for this event and switches to the
                // calendar tab + opens the appointment editor.
                window.dispatchEvent(new CustomEvent('rescheduleFromTask', {
                  detail: {
                    task_id: value.task_id,
                    appointment_id: value.appointment_id || field?._appointment_id || null,
                  },
                }));
              }}
              data-testid="create-task-field-reschedule-btn"
            >
              Reschedule
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
            onClick={() => {
              // App.js listens for this and pops the Task dialog.
              window.dispatchEvent(new CustomEvent('openTaskDrawer', {
                detail: { task_id: value.task_id },
              }));
            }}
            data-testid="create-task-field-open-btn"
          >
            Open Task
          </Button>
        </div>
      </div>
    );
  }

  // Default placeholder values for the editable form.
  const titlePlaceholder = (field?.task_title_template || 'Follow-up')
    .replace('{client_name}', client?.full_name || client?.first_name || 'Client');
  const descLabel = field?.description_label || 'Details';
  // Jun 2026 — due date is optional per task type; when optional we
  // don't force-fill a default and the backend won't stamp +14d.
  const dueRequired = field?.due_date_required !== false;
  const defaultDueOffset = parseInt(field?.default_due_offset_days, 10);
  const defaultDue = Number.isFinite(defaultDueOffset)
    ? todayPlus(defaultDueOffset)
    : todayPlus(14);

  // Reschedule-timing dropdown — provider picks a structured option
  // (e.g. "2 weeks") instead of typing free text.  The template
  // designer can pare down the option list via ``field.reschedule_options``
  // (Mar 2026); if not configured, we offer the full default set so
  // existing templates get the feature for free.
  const isReschedule = (field?.action_type || 'reschedule_appointment') === 'reschedule_appointment';
  const rescheduleOptions = Array.isArray(field?.reschedule_options) && field.reschedule_options.length > 0
    ? field.reschedule_options
    : DEFAULT_RESCHEDULE_OPTIONS;

  const onPickRescheduleIn = (key) => {
    const opt = rescheduleOptions.find((o) => o.key === key)
      || RESCHEDULE_OPTIONS_BY_KEY[key]
      || null;
    const next = { ...(value || {}) };
    if (!key) {
      delete next.reschedule_in;
      delete next.reschedule_in_label;
      delete next.reschedule_in_days;
    } else {
      next.reschedule_in = key;
      next.reschedule_in_label = opt?.label || key;
      next.reschedule_in_days = Number.isFinite(opt?.days) ? opt.days : null;
      // Auto-update the task's `due_date` to match the picked offset
      // so the scheduler's "Follow up by" date stays consistent with
      // the structured choice — provider can still tweak manually.
      if (Number.isFinite(opt?.days) && opt.days >= 0) {
        next.due_date = todayPlus(opt.days);
      }
    }
    onChange(next);
  };

  return (
    <div
      className="border-l-4 border-purple-400 bg-purple-50 rounded-lg p-3 my-2 space-y-2"
      data-testid="create-task-field-widget"
    >
      <div className="text-xs font-semibold text-purple-800 uppercase tracking-wide">
        📋 {field?.label || 'Create Task on Save'}
      </div>

      {isReschedule && (
        <div>
          <Label className="text-xs text-slate-700">
            When should the client be rescheduled? <span className="text-red-500">*</span>
          </Label>
          <select
            value={value?.reschedule_in || ''}
            onChange={(e) => onPickRescheduleIn(e.target.value)}
            disabled={readOnly}
            className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
            data-testid="create-task-reschedule-in-select"
          >
            <option value="">— Pick timing —</option>
            {rescheduleOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
          {!value?.reschedule_in && !readOnly && (
            <p className="text-[10px] text-amber-700 mt-1" data-testid="create-task-reschedule-required-hint">
              Required — the scheduler task is created once a timing is picked; the note can't be signed without one.
            </p>
          )}
          {value?.reschedule_in && (
            <p className="text-[10px] text-purple-700 mt-1" data-testid="create-task-reschedule-in-hint">
              Scheduler will see: <span className="font-semibold">
                Reschedule in {value.reschedule_in_label || value.reschedule_in}
              </span>
              {Number.isFinite(value?.reschedule_in_days) && (
                <span className="text-slate-600">
                  {' '}· target ~{computeTargetDate(new Date().toISOString().slice(0, 10), value.reschedule_in_days)}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      <div>
        <Label className="text-xs text-slate-700">Title</Label>
        <Input
          type="text"
          value={value?.title ?? ''}
          placeholder={titlePlaceholder}
          disabled={readOnly}
          onChange={(e) => onChange({ ...(value || {}), title: e.target.value })}
          data-testid="create-task-title-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-700">Assign to</Label>
          <select
            value={value?.assigned_to || ''}
            onClick={ensureUsersLoaded}
            onFocus={ensureUsersLoaded}
            onChange={(e) => onChange({
              ...(value || {}),
              assigned_to: e.target.value,
              _assigned_to_name_cache: undefined,
            })}
            disabled={readOnly}
            className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
            data-testid="create-task-assignee-select"
          >
            {value?.assigned_to && !users.some((u) => u.id === value.assigned_to) && (
              <option value={value.assigned_to}>
                {resolvedAssigneeName || value._assigned_to_name_cache || value.assigned_to}
              </option>
            )}
            <option value="">— Pick a user —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs text-slate-700">Due Date{dueRequired ? '' : ' (optional)'}</Label>
          <Input
            type="date"
            value={value?.due_date || (dueRequired ? defaultDue : '')}
            disabled={readOnly}
            onChange={(e) => onChange({ ...(value || {}), due_date: e.target.value })}
            data-testid="create-task-duedate-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-700">{descLabel}</Label>
        <Textarea
          value={value?.description || ''}
          placeholder="e.g. Mom said only mornings work; can reach @ 617-xxx-xxxx"
          rows={2}
          disabled={readOnly}
          onChange={(e) => onChange({ ...(value || {}), description: e.target.value })}
          data-testid="create-task-description-input"
        />
      </div>

      <div className="text-[10px] text-purple-700">
        Task will be created when you save the note.
        {dueRequired && (
          <>
            {' '}Default due-date offset:{' '}
            <span className="font-mono">+{Number.isFinite(defaultDueOffset) ? defaultDueOffset : 14}d</span>.
          </>
        )}
      </div>
    </div>
  );
};

export default CreateTaskFieldWidget;
